import CircleIcon from '@mui/icons-material/Circle';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import MuiLink from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from 'components/Button/Button';
import Spin from 'components/Spin';
import Form from 'components/form';
import Checkbox from 'components/form/Checkbox';
import PhoneInput from 'components/form/PhoneInput';
import Input from 'components/form/TextInput';
import useRequest from 'hooks/useRequest';
import { Logo } from 'modules/settings/containers/AccountOverview';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getKickstartGroups } from 'services/groups.service';
import toast from 'utils/toast';

const FormItem = Form.FormItem;

function getValueFromFileEvent({ target }) {
  return {
    target,
  };
}

function getFileValueProps(value) {
  if (value && value.target) {
    return {
      value: value.target.value,
    };
  }
  return {
    value,
  };
}

const TICForm = ({
  type,
  onSubmit,
  submitting,
  form: { getFieldDecorator, validateFields },
}) => {
  const account = useSelector((state) => state.settings.account);
  const [t] = useTranslation('igniterkit' + type);
  const [groups, { loading, error }] = useRequest(getKickstartGroups, {
    type: type === 'easter' ? 'kickstart_easter' : 'kickstart',
    brand: 'tic',
  });

  const groupOptions = groups?.map((i) => ({
    label: i.group_name,
    value: i.group_id,
    hint: i.group_comments,
  }));

  const handleSubmit = () => {
    validateFields().then(
      (values) => onSubmit(values, groupOptions),
      () => {
        toast.error('Please complete all the required fields');
      },
    );
  };

  return (
    <React.Fragment>
      <div className="mb-4">
        <Grid container spacing={2}>
          <Grid item>
            <Typography variant="h1">
              {t('igniter-kit-account-set-up', 'Igniter Kit Account Set-up')}
            </Typography>
            <Typography variant="h4">
              {t(
                `let-start-tic-account`,
                `Let's Get your Text In Church Account started`,
              )}
            </Typography>
          </Grid>
        </Grid>
      </div>
      <Box mb={4} maxWidth={800}>
        {error && <Alert severity="error">{error}</Alert>}
        <Form>
          <Grid container spacing={2}>
            <Grid item>
              <div>
                <p>
                  {t(
                    'recommend-msg-and-email-multiple-person',
                    'We recommend your emails and text messages come from more than one person. Please include the contact info for those two people.',
                  )}
                </p>
              </div>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography gutterBottom fontWeight={500} variant="subtitle1">
                  Account Branding
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Paper
                    variant="outlined"
                    sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2 }}
                  >
                    {account.account_color && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        mr={2}
                      >
                        <CircleIcon
                          sx={{
                            color: '#' + account.account_color,
                            fontSize: 50,
                          }}
                        />
                        <Typography color="text.secondary" variant="body2">
                          #{account.account_color}
                        </Typography>
                      </Stack>
                    )}
                    <Logo hash={account.account_logo_url} />
                  </Paper>
                  <Link target="_blank" to="/settings/editaccount">
                    <Button>Edit</Button>
                  </Link>
                </Stack>
              </Box>
              <div className="form-header">
                <h3>
                  {t(
                    'guest-primary-contact',
                    'Guest Follow-up Primary Contact',
                  )}
                </h3>
                <p>
                  {t(
                    'primary-contact-description',
                    'The Primary Contact should be the main person that correspondence will come from. We recommend your emails and text messages come from more than one person.Please include the contact info for those two people.',
                  )}
                </p>
              </div>
              <Grid container spacing={2}>
                <Grid item md={6}>
                  <FormItem>
                    {getFieldDecorator('first_name', {
                      initialValue: '',
                      rules: [{ required: true, message: 'Required' }],
                    })(<Input fullWidth label="First Name" />)}
                  </FormItem>
                </Grid>
                <Grid item md={6}>
                  <FormItem>
                    {getFieldDecorator('last_name', {
                      initialValue: '',
                      rules: [{ required: true, message: 'Required' }],
                    })(<Input fullWidth label="Last Name" />)}
                  </FormItem>
                </Grid>
              </Grid>
              <div className="field">
                <FormItem>
                  {getFieldDecorator('email', {
                    initialValue: '',
                    rules: [
                      { required: true, type: 'email', message: 'Required' },
                    ],
                  })(
                    <Input
                      fullWidth
                      label="Email"
                      placeholder="pastor@example.com"
                      type="email"
                    />,
                  )}
                </FormItem>
              </div>
              <Grid container spacing={2}>
                <Grid item md={6}>
                  <FormItem>
                    {getFieldDecorator('salutation', {
                      initialValue: '',
                      rules: [{ required: true, message: 'Required' }],
                    })(
                      <Input
                        fullWidth
                        label="Salutation"
                        placeholder="Sincerely, Grace and Peace, Blessings"
                        hint="Example: Sincerely, Grace and Peace, Blessings"
                      />,
                    )}
                  </FormItem>
                </Grid>
                <Grid item md={6}>
                  <FormItem>
                    {getFieldDecorator('position_at_church', {
                      initialValue: '',
                      rules: [{ required: true, message: 'Required' }],
                    })(
                      <Input
                        fullWidth
                        label="Position at the Church"
                        placeholder="Senior Pastor"
                        hint="Example: Senior Pastor"
                      />,
                    )}
                  </FormItem>
                </Grid>
              </Grid>
              <Divider sx={{ my: 3 }} />
              <div className="form-header">
                <h3>
                  {t(
                    'guest-secondary-contact',
                    'Guest Follow-up Secondary Contact',
                  )}
                </h3>
                <small>
                  {t(
                    'secondary-contact-description',
                    'The Secondary Contact is someone who sends less messages than the Primary Contact, often an office staff contact.',
                  )}
                </small>
              </div>
              <Grid container spacing={2}>
                <Grid item md={6}>
                  <FormItem>
                    {getFieldDecorator('first_name_2', {
                      initialValue: '',
                      rules: [{ required: false }],
                    })(<Input fullWidth label="First Name #2" />)}
                  </FormItem>
                </Grid>
                <Grid item md={6}>
                  <FormItem>
                    {getFieldDecorator('last_name_2', {
                      initialValue: '',
                      rules: [{ required: false }],
                    })(<Input fullWidth label="Last Name #2" />)}
                  </FormItem>
                </Grid>
              </Grid>
              <div className="field">
                <FormItem>
                  {getFieldDecorator('email_2', {
                    initialValue: '',
                    rules: [{ required: false, type: 'email' }],
                  })(
                    <Input
                      fullWidth
                      label="Email #2"
                      placeholder="pastor@example.com"
                      type="email"
                    />,
                  )}
                </FormItem>
              </div>
              <Grid container spacing={2}>
                <Grid item md={6}>
                  <FormItem>
                    {getFieldDecorator('salutation_2', {
                      initialValue: '',
                      rules: [{ required: false }],
                    })(
                      <Input
                        fullWidth
                        label="Salutation #2"
                        placeholder="Sincerely, Grace and Peace, Blessings"
                        hint="Example: Sincerely, Grace and Peace, Blessings"
                      />,
                    )}
                  </FormItem>
                </Grid>
                <Grid item md={6}>
                  <FormItem>
                    {getFieldDecorator('position_at_church_2', {
                      initialValue: '',
                      rules: [{ required: false }],
                    })(
                      <Input
                        fullWidth
                        label="Position at the Church #2"
                        placeholder="Senior Pastor"
                        hint="Example: Senior Pastor"
                      />,
                    )}
                  </FormItem>
                </Grid>
              </Grid>
              <Divider sx={{ my: 3 }} />
              <div className="form-header">
                <h3>{t('church-info', 'Church Information')}</h3>
              </div>
              <div className="field">
                <FormItem>
                  {getFieldDecorator('church_name', {
                    initialValue: '',
                    rules: [{ required: true, message: 'Required' }],
                  })(<Input fullWidth label="Church Name" />)}
                </FormItem>
              </div>
              <div className="field">
                <FormItem>
                  {getFieldDecorator('church_phone', {
                    initialValue: '',
                    rules: [{ required: true, message: 'Required' }],
                  })(<PhoneInput label="Church Phone Number" />)}
                </FormItem>
              </div>
              <div className="field">
                <FormItem>
                  {getFieldDecorator('coffee_with_pastors', {
                    initialValue: '',
                    rules: [{ required: false }],
                  })(
                    <Input
                      fullWidth
                      label="When, where and what time will Coffee with the Pastors be held?"
                      hint={
                        'Example: "On the 4th Sunday of the month at 9am in the parlor"'
                      }
                    />,
                  )}
                </FormItem>
              </div>
              <div className="field">
                <FormItem>
                  {getFieldDecorator('service_times', {
                    initialValue: '',
                    rules: [{ required: false }],
                  })(
                    <Input
                      fullWidth
                      label={t('service-time-label', 'Service Time(s)')}
                      hint={'Example: "Sundays at 9am and 11am"'}
                    />,
                  )}
                </FormItem>
              </div>
              <div className="field">
                <FormItem>
                  {getFieldDecorator('service_address', {
                    initialValue: '',
                    rules: [{ required: false }],
                  })(
                    <Input
                      fullWidth
                      label={t(
                        'service-address-label',
                        'Address of Service Location',
                      )}
                    />,
                  )}
                </FormItem>
              </div>
              <div className="field">
                <FormItem>
                  {getFieldDecorator('nursery_details', {
                    initialValue: '',
                    rules: [{ required: false }],
                  })(
                    <Input
                      fullWidth
                      label={t('nursery-details-label', 'Nursery Details')}
                      hint="Ages, when offered, etc"
                    />,
                  )}
                </FormItem>
              </div>
              <div className="field">
                <FormItem>
                  {getFieldDecorator('sunday_school_details', {
                    initialValue: '',
                    rules: [{ required: false }],
                  })(
                    <Input
                      fullWidth
                      label={t(
                        'sunday-school-details-label',
                        'Sunday School Details',
                      )}
                      hint="Time, ages for classes, type of activities"
                    />,
                  )}
                </FormItem>
              </div>
              <div className="field">
                <FormItem>
                  {getFieldDecorator('outfit_details', {
                    initialValue: '',
                    rules: [{ required: false }],
                  })(
                    <Input
                      fullWidth
                      label={t('outfit-details-label', 'Outfit Details')}
                      hint="Dress style to tell pre-registered guests"
                    />,
                  )}
                </FormItem>
              </div>
              <div className="field">
                <FormItem>
                  {getFieldDecorator('guest_services', {
                    initialValue: '',
                    rules: [{ required: false }],
                  })(
                    <Input
                      fullWidth
                      label={t(
                        'guest-services-label',
                        'Guest Services Offered',
                      )}
                      hint="Designated parking, hospitality table, gift"
                    />,
                  )}
                </FormItem>
              </div>
              <div className="field">
                <FormItem>
                  {getFieldDecorator('church_website_url', {
                    initialValue: '',
                    rules: [{ required: false }],
                  })(
                    <Input
                      fullWidth
                      label="Church Website URL"
                      placeholder="http://"
                    />,
                  )}
                </FormItem>
              </div>
              <div className="field">
                <FormItem>
                  {getFieldDecorator('facebook_page_url', {
                    initialValue: '',
                    rules: [{ required: false }],
                  })(
                    <Input
                      fullWidth
                      label="Facebook Page URL"
                      placeholder="http://"
                    />,
                  )}
                </FormItem>
              </div>
              <div className="field">
                <FormItem>
                  {getFieldDecorator('giving_url', {
                    initialValue: '',
                    rules: [{ required: false }],
                  })(
                    <Input
                      fullWidth
                      label="Giving URL"
                      placeholder="http://"
                    />,
                  )}
                </FormItem>
              </div>
              <div className="field">
                <FormItem>
                  {getFieldDecorator('csv_import', {
                    rules: [{ required: false }],
                    getValueFromEvent: getValueFromFileEvent,
                    getValueProps: getFileValueProps,
                  })(
                    <Input
                      label={
                        <>
                          <div>Import Current Members</div>
                          <Typography fontWeight={400}>
                            You can download the sample template and use it to
                            import contacts.{' '}
                            <MuiLink href="/assets/downloads/Import-Template.csv">
                              {t('settings:import-download-template')}
                            </MuiLink>
                          </Typography>
                        </>
                      }
                      type="file"
                      name="csv_import"
                      accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                      hint="If you have a CSV or Excel file of your current members?"
                    />,
                  )}
                </FormItem>
              </div>
              <Divider sx={{ my: 3 }} />
              <h3>{t('select-groups', 'Select Groups')}</h3>
              <p>
                {t(
                  'select-group-you-would-like-to-create-for-you',
                  'Please select the groups you would like us to create for you.',
                )}
              </p>
              <Spin spinning={loading} />
              {groupOptions?.map((g) => (
                <FormItem>
                  {getFieldDecorator(`groups.${g.value}`, {
                    valuePropName: 'checked',
                  })(
                    <Checkbox
                      fullWidth
                      value={g.value}
                      sx={{
                        '& label': {
                          alignItems: 'flex-start',
                        },
                      }}
                      label={
                        <Stack spacing={0.4} mb={1} pt={1}>
                          <Typography>{g.label}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {g.hint}
                          </Typography>
                        </Stack>
                      }
                    />,
                  )}
                </FormItem>
              ))}
              <Divider sx={{ my: 3 }} />
              <Button size="large" onClick={handleSubmit} loading={submitting}>
                {t(`let's-started`, `Let's Get Started`)}
              </Button>
            </Grid>
          </Grid>
        </Form>
      </Box>
    </React.Fragment>
  );
};

export default Form.create()(TICForm);
